import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { AuthService } from './services/guard/auth.service';
import { LocalService } from './services/storage/local.service';
import { SsoServiceService } from './services/sso-service.service';

@Injectable({
  providedIn: 'root',
})
export class AppInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  constructor(
    private readonly authService: AuthService,
    private readonly localService: LocalService,
    private readonly ssoService: SsoServiceService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Exclude JSON files from interceptor
    if (request.url.endsWith('json')) {
      let clonedRequest = request.clone({
        setHeaders: {
          'Cache-Control': 'public, max-age=3600',
        },
      });
      return next.handle(clonedRequest).pipe(
        catchError((error) => this.handleHttpError(error))
      );
    }
    // If authenticated, add token to request
    if (this.authService.isAuthenticated()) {
      if (this.authService.authenticationType == 'keycloak') {
        if (this.authService.isTokenExpired) {
          return this.handleTokenRefresh(request, next);
        } else {
          return this.addTokenToRequest(request, next);
        }
      } else if (this.ssoService.isTokenExpired()) {
        return this.handleSsoTokenRefresh(request, next);
      } else {
        return this.addTokenToRequest(request, next);
      }
    }

    return next.handle(request).pipe(
      catchError((error) => this.handleHttpError(error))
    );
  }

  private handleHttpError(error: HttpErrorResponse): Observable<never> {
    if (error.status === 403) {
      console.warn('Erreur 403 détectée → déconnexion automatique.');
      this.authService.logout();
    }
    if (error.status === 401) {
      console.warn('Erreur 401 détectée → déconnexion automatique.');
      this.authService.logout();
    }

    return throwError(() => error);
  }

  private handleSsoTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Prevent multiple simultaneous refresh attempts

    return from(this.ssoService.refreshToken()).pipe(
      mergeMap((res) => {
        return this.addTokenToRequest(request, next);
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.authService.logout()
        return throwError(() => error);
      }),
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.authService.updateToken().pipe(
      mergeMap((res) => {
        return this.addTokenToRequest(request, next);
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.authService.logout()
        return throwError(() => error);
      }),
    );
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = `Bearer ${this.authService.getToken()}`;
    let clonedRequest = request.clone({
      setHeaders: {
        Authorization: token,
        isSignIn: 'false',
      },
    });
    if (request.url.endsWith('locales') || request.url.endsWith('categories')) {
      clonedRequest = request.clone({
        setHeaders: {
          Authorization: token,
          'Cache-Control': 'public, max-age=3600',
          isSignIn: 'false',
        },
      });
    }

    // Add sign-in header if needed
    if (!this.localService.getItem('login')) {
      this.localService.setItem('login', 'login');
      clonedRequest = request.clone({
        setHeaders: {
          Authorization: token,
          isSignIn: 'true',
        },
      });
    }

    return next.handle(clonedRequest);
  }

  private handleAzureAuthentication(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return from(this.ssoService.getToken()).pipe(
      switchMap((msalToken) => {
        let clonedRequest = request.clone({
          setHeaders: {
            Authorization: msalToken,
            isSignIn: 'false',
          },
        });

        if (!this.localService.getItem('login')) {
          this.localService.setItem('login', 'login');
          clonedRequest = request.clone({
            setHeaders: {
              Authorization: msalToken,
              isSignIn: 'true',
            },
          });
        }
        return next.handle(clonedRequest);
      }),
    );
  }
}
